import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useSelector } from '_common/hooks';
import { useGetReferenceStylesListQuery } from '../../ReferenceStylesApi';
import { useFilterSelector } from '_common/components/Filters/FilterSlice';

import { EmptyFilteredState } from '_common/components';
import { Table, Cell, type TableColumn, type TableRow } from '_common/components/Table';

import RefStyleEmptyState from './RefStyleEmptyState/RefStyleEmptyState';
import ActionsCell from './Cells/ActionsCell/ActionsCell';
import NameCell from './Cells/NameCell/NameCell';

import styles from './RefStyleTable.module.scss';

type RefStyleTableProps = {
  category: Template['category'];
  search: string;
};

const RefStyleTable = ({ category, search }: RefStyleTableProps) => {
  const intl = useIntl();

  const { data, isFetching } = useGetReferenceStylesListQuery();

  const { selectFilterParams } = useFilterSelector();
  const filterParams = useSelector((state) =>
    selectFilterParams(state, state.filters[`referenceStyles-${category}`]),
  );

  const filteredRefStyles = useMemo(() => {
    if (!data) {
      return [];
    }
    let filteredRefStyles = data.order;

    // Filters by name
    if (search !== '') {
      filteredRefStyles = filteredRefStyles.filter((refStyleId) =>
        data.extensions[refStyleId].name.toLowerCase().includes(search.toLowerCase()),
      );
    }

    // Filter by category
    filteredRefStyles = filteredRefStyles.filter(
      (refStyleId) => data.extensions[refStyleId].category === category,
    );

    // Filters by status if any filter is active
    return filterParams.filter_fields.length > 0
      ? filteredRefStyles.filter((refStyleId) =>
          filterParams.filter_fields.includes(data.extensions[refStyleId].status),
        )
      : filteredRefStyles;
  }, [data, search, filterParams]);

  const columns = useMemo<TableColumn<NonNullable<typeof data>['extensions'][number]>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        size: 200,
        header: intl.formatMessage({ id: 'global.name' }),
        cell: (info) => {
          const refStyle = info.row.original;

          return (
            <Cell testId={`${refStyle.id}-name-column`}>
              <NameCell refStyleId={refStyle.id} />
            </Cell>
          );
        },
      },
      {
        id: 'description',
        accessorKey: 'description',
        enableSorting: false,
        header: intl.formatMessage({ id: 'global.description' }),
        cell: (info) => {
          const refStyle = info.row.original;
          return (
            <Cell testId={`${refStyle.id}-description-column`}>
              {refStyle.description ? (
                refStyle.description
              ) : (
                <Cell.Empty>{intl.formatMessage({ id: 'NO_DESCRIPTION' })}</Cell.Empty>
              )}
            </Cell>
          );
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        size: 198,
        cell: (info) => {
          const refStyle = info.row.original;
          return (
            <Cell testId={`${refStyle.id}-actions-column`}>
              <ActionsCell refStyleId={refStyle.id} />
            </Cell>
          );
        },
      },
    ],
    [],
  );

  const rowsData = useMemo(
    () =>
      data
        ? filteredRefStyles.map<TableRow<NonNullable<(typeof data)['extensions']>[number]>>(
            (refStyleId) => {
              return data.extensions[refStyleId];
            },
          )
        : [],
    [filteredRefStyles, data],
  );

  if (filteredRefStyles.length === 0) {
    return filterParams.filter_fields.length > 0 ? (
      <EmptyFilteredState identity={`referenceStyles-${category}`} margin="8rem 0" />
    ) : (
      <RefStyleEmptyState />
    );
  }

  return (
    <div className={styles.root}>
      <Table
        identity="refStyles"
        options={{ columns: columns, data: rowsData }}
        renderFooter={() => null}
        isLoading={isFetching}
        loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
        testId={`referenceStyles-${category}`}
        selectable={false}
        virtualized={false}
        height={'100%'}
      />
    </div>
  );
};

export default RefStyleTable;
